import { useCallback, useRef } from "react";
import { EDUGATE_API_URL } from "../config/serviceConfig";

/**
 * 通知系统业务逻辑 Hook
 * 封装通知的获取、新增、标记已读等操作
 */
const useNotificationHandlers = ({
  currentUser,
  notifications,
  setNotifications,
  unreadNotificationCount,
  setUnreadNotificationCount,
  setCurrentToastNotification,
}) => {
  // 使用 ref 存储 fetchNotifications 的引用，避免循环依赖
  const fetchNotificationsRef = useRef(null);

  // ✅ 处理新通知（来自 WebSocket 推送）
  const handleNewNotification = useCallback((notification) => {
    console.log('🔔 处理新通知:', notification);
    console.log('🔍 通知详情:', {
      id: notification.id,
      type: notification.notification_type || notification.type,
      title: notification.title,
      parent_id: notification.parent_id,
      sender_name: notification.sender_name,
      is_read: notification.is_read,
      '是否为回复': !!notification.parent_id
    });
    
    // 统一字段名：WebSocket推送使用notification_type，需要转换为type
    const normalizedNotification = {
      ...notification,
      type: notification.notification_type || notification.type,
      is_read: false  // 🔧 新推送的通知默认为未读
    };
    
    // 🔧 判断是否为回复消息
    if (notification.parent_id) {
      // 这是一条回复消息，先添加到列表，稍后在 fetchNotifications 中处理根消息状态
      console.log('💬 收到回复消息，parent_id:', notification.parent_id);
      
      // 🔧 不在这里处理根消息状态，因为根消息可能还没加载
      // 只是简单添加回复消息到列表
      setNotifications(prev => [normalizedNotification, ...prev]);
      
      // 🔧 触发一次 fetchNotifications，让服务器数据更新根消息的 reply_count
      // 并在合并时正确处理未读数量
      setTimeout(() => {
        if (fetchNotificationsRef.current) {
          fetchNotificationsRef.current(false);
        }
      }, 100);
      
    } else {
      // 这是一条新的根消息，增加红点数量
      console.log('✅ 新根消息，未读数量+1');
      setUnreadNotificationCount(prev => prev + 1);
      
      // 添加到通知列表
      setNotifications(prev => [normalizedNotification, ...prev]);
    }
    
    // 显示Toast提示
    setCurrentToastNotification({
      ...normalizedNotification,
      timestamp: Date.now()
    });
    
    // 3秒后自动隐藏Toast
    setTimeout(() => {
      setCurrentToastNotification(null);
    }, 3000);
    
    // 如果是紧急通知，可以播放音效或其他提示
    if (notification.level === 'urgent') {
      console.log('⚠️ 紧急通知！');
      // TODO: 播放通知音效
    }
  }, [setNotifications, setUnreadNotificationCount, setCurrentToastNotification]);

  // ✅ 获取通知列表（120天内最近50条，含已读状态）
  const fetchNotifications = useCallback(async (forceUpdate = false) => {
    if (!currentUser) {
      console.log('⚠️ fetchNotifications: currentUser 为空，跳过');
      return;
    }
    
    console.log('📥 开始获取通知列表...', { userId: currentUser.unified_user_id || currentUser.username, forceUpdate });
    
    try {
      const userId = currentUser.unified_user_id || currentUser.username;
      const response = await fetch(`${EDUGATE_API_URL}/api/user/notifications?user_id=${userId}&days=120`);
      const data = await response.json();
      
      if (data.code === 0 && data.data) {
        const fetchedNotifications = data.data;
        
        // 🔧 智能合并：保留本地 WebSocket 推送的新通知
        setNotifications(prev => {
          if (forceUpdate || prev.length === 0) {
            // 首次加载或强制更新，直接使用服务器数据
            console.log('📥 首次加载通知列表');
            return fetchedNotifications;
          }
          
          // 合并逻辑：保留本地新增的通知（可能是 WebSocket 刚推送的）
          const fetchedIds = new Set(fetchedNotifications.map(n => n.id));
          const localNewNotifications = prev.filter(n => !fetchedIds.has(n.id));
          
          if (localNewNotifications.length > 0) {
            console.log(`🔄 合并 ${localNewNotifications.length} 条本地新通知`);
            console.log('🔍 本地新通知 IDs:', localNewNotifications.map(n => n.id));
            console.log('🔍 服务器通知 IDs:', Array.from(fetchedIds));
            return [...localNewNotifications, ...fetchedNotifications];
          }
          
          // 没有本地新通知，直接使用服务器数据
          return fetchedNotifications;
        });
        
        // 🔧 重新计算未读数量（只统计根消息的未读数量）
        // 使用 setTimeout 确保 setNotifications 完成后再计算
        setTimeout(() => {
          setNotifications(current => {
            // 🔧 只统计根消息（parent_id 为空）的未读数量
            const unreadRootMessages = current.filter(n => !n.parent_id && !n.is_read);
            const unreadRootCount = unreadRootMessages.length;
            console.log(`📊 重新计算未读数量（只统计根消息）: ${unreadRootCount}`);
            console.log(`📋 未读根消息列表:`, unreadRootMessages.map(n => ({
              id: n.id,
              title: n.title,
              is_read: n.is_read,
              created_at: n.created_at
            })));
            setUnreadNotificationCount(unreadRootCount);
            return current;
          });
        }, 0);
      }
    } catch (error) {
      console.error('获取通知列表失败:', error);
    }
  }, [currentUser, setNotifications, setUnreadNotificationCount]);

  // 绑定 fetchNotifications 到 ref，供 handleNewNotification 使用
  fetchNotificationsRef.current = fetchNotifications;

  // ✅ 标记单条通知为已读（不移除，只更新状态）
  const handleMarkAsRead = useCallback(async (notificationId) => {
    if (!currentUser) return;
    
    try {
      const userId = currentUser.unified_user_id || currentUser.username;
      
      // 🔍 调试：检查当前消息状态
      const currentNotification = notifications.find(n => n.id === notificationId);
      console.log('🔍 准备标记已读:', {
        id: notificationId,
        当前状态: currentNotification?.is_read ? '已读' : '未读',
        当前未读数: unreadNotificationCount
      });
      
      await fetch(`${EDUGATE_API_URL}/api/user/notifications/${notificationId}/read?user_id=${userId}`, {
        method: 'POST'
      });
      
      // 更新本地状态：标记为已读，不移除
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      ));
      
      // 🔧 只有当消息之前是未读状态时，才减少未读数量
      if (currentNotification && !currentNotification.is_read) {
        console.log('✅ 未读数量 -1');
        setUnreadNotificationCount(prev => Math.max(0, prev - 1));
      } else {
        console.log('⚠️ 消息已经是已读状态，未读数量不变');
      }
    } catch (error) {
      console.error('标记已读失败:', error);
    }
  }, [currentUser, notifications, unreadNotificationCount, setNotifications, setUnreadNotificationCount]);

  // ✅ 标记全部通知为已读（不清空，全部变为已读状态）
  const handleMarkAllAsRead = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const userId = currentUser.unified_user_id || currentUser.username;
      await fetch(`${EDUGATE_API_URL}/api/user/notifications/read-all?user_id=${userId}&days=120`, {
        method: 'POST'
      });
      
      // 更新本地状态：全部标记为已读
      const now = new Date().toISOString();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: now })));
      setUnreadNotificationCount(0);
    } catch (error) {
      console.error('全部标记已读失败:', error);
    }
  }, [currentUser, setNotifications, setUnreadNotificationCount]);

  return {
    handleNewNotification,
    fetchNotifications,
    handleMarkAsRead,
    handleMarkAllAsRead,
  };
};

export default useNotificationHandlers;
